import { CacheLocation, FingerprintJSPro, FpjsClient } from '@fingerprintjs/fingerprintjs-pro-spa'
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs'
import { v4 as uuidv4 } from 'uuid'
import { glovoRum } from '../o11y/client'
import { getConfig } from '@/tools/config'

let fpjsClient: FpjsClient | null = null
let tmjsClient: Thumbmark | null = null

type FingerPrintProvider = 'CWUID' | 'FingerprintJS' | 'ThumbmarkJS'
export type FingerPrint = { provider: FingerPrintProvider; fingerprint: string }

export const initFingerprint = async () => {
  if (fpjsClient) {
    return fpjsClient
  }
  const { fingerprintJS, env } = getConfig()
  if (!fingerprintJS) {
    throw new Error('FingerprintJS configuration is not provided')
  }
  const { key, behaviorPath, agentDownloadPath, getResultPath } = fingerprintJS
  const host = env === 'development' ? 'https://testglovo.com' : ''

  const client = new FpjsClient({
    loadOptions: {
      apiKey: key,
      endpoint: [
        `${host}/${behaviorPath}/${getResultPath}?region=eu`,
        FingerprintJSPro.defaultEndpoint,
      ],
      scriptUrlPattern: [
        `${host}/${behaviorPath}/${agentDownloadPath}/?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
        FingerprintJSPro.defaultScriptUrlPattern,
      ],
      region: 'eu',
    },
    cacheLocation: CacheLocation.LocalStorage,
  })
  try {
    await client.init()
    fpjsClient = client
  } catch (e) {
    if (getConfig().env !== 'development') {
      throw e
    }
  }
  return client
}

export const initThumbmark = async () => {
  if (tmjsClient) {
    return tmjsClient
  }
  const thumbmarkClient = new Thumbmark()
  tmjsClient = thumbmarkClient
}

export const getFingerprint = async (
  provider: 'FingerprintJS' | 'ThumbmarkJS',
  userId?: string
) => {
  try {
    glovoRum.addAction('customer_web.fingerprint_requested', {
      provider,
      userId,
    })
    if (provider === 'ThumbmarkJS') {
      return await getThumbmark()
    }
    if (!fpjsClient) {
      if (getConfig().env === 'development') {
        return getGlovoFingerPrint().fingerprint
      }
      throw new Error('FingerprintJS client not initialized')
    }

    const { visitorId } = await fpjsClient.getVisitorData({
      linkedId: userId,
    })
    return visitorId
  } catch (error) {
    glovoRum.addAction('customer_web.fingerprint_failed', {
      provider,
      userId,
    })
    throw error
  }
}

const getThumbmark = async () => {
  if (!tmjsClient) {
    throw new Error('ThumbmarkJS client not initialised')
  }
  const { thumbmark } = await tmjsClient.get()
  return thumbmark
}

export const getGlovoFingerPrint = (): FingerPrint => {
  const UUID_KEY = 'glv_uuid'
  const storedUuid = localStorage.getItem(UUID_KEY)
  const uuid = storedUuid || uuidv4()
  if (!storedUuid) {
    localStorage.setItem(UUID_KEY, uuid)
  }
  return { provider: 'CWUID', fingerprint: uuid }
}
